// Voorstel voor een extra werk uit de opstartfiche van het gelinkte project (SPEC_wijzigingen 3.3).
// Rekent met de prijslijst van déze onderaannemer en de gewone rekenkern — nooit met de verkoopprijs
// aan de klant. Bevestigen kan enkel door Eranda, Timen, Ervin of Jordy; nadien blijft het bedrag aanpasbaar.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { weergaveRegels, type WeergaveRij } from '@recotex/shared';
import { api, ApiError } from '../lib/api';
import { fmtEUR, fmtNum, fmtDate } from '../lib/format';

interface Voorstel {
  bedrag: number;
  bronReferentie: string;
  bronLabel: string;
  omschrijving: string;
  prijslijst: string;
  regels: WeergaveRij[];
  nietGekoppeld: string[];
}

export function BouwflowVoorstelModal({
  projectNummer,
  ploegId,
  magBevestigen,
  onBevestig,
  onSluit,
}: {
  projectNummer: string;
  ploegId: string;
  magBevestigen: boolean;
  onBevestig: (v: { bedrag: number; bronReferentie: string; omschrijving: string }) => void;
  onSluit: () => void;
}) {
  const [upload, setUpload] = useState<Voorstel | null>(null);
  const [uploaden, setUploaden] = useState(false);
  const [uploadFout, setUploadFout] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['bouwflow-voorstel', projectNummer, ploegId],
    queryFn: () => api.projects.bouwflowVoorstel(projectNummer, ploegId),
    retry: false,
  });
  const geenFiche = q.error instanceof ApiError && q.error.status === 404;
  const foutTekst = q.error instanceof ApiError && q.error.payload && typeof q.error.payload === 'object' && 'error' in q.error.payload
    ? String((q.error.payload as { error: string }).error)
    : null;

  const uitServer: Voorstel | null = q.data
    ? {
        bedrag: q.data.bedrag,
        bronReferentie: `calculatie:${q.data.bron.calcId}`,
        bronLabel: `Opstartfiche ${q.data.bron.pdfFilename || ''} (calculatie van ${fmtDate(q.data.bron.createdAt)}, ${q.data.bron.klantNaam})`,
        omschrijving: `Werken project ${projectNummer} volgens opstartfiche`,
        prijslijst: q.data.prijslijst + (q.data.gevelVariant ? ` · gevel variant ${q.data.gevelVariant}` : ''),
        regels: [...q.data.weergave.werkposten, ...q.data.weergave.meerwerk],
        nietGekoppeld: q.data.nietGekoppeld,
      }
    : null;
  const voorstel = upload ?? uitServer;

  const laadOp = async (file: File) => {
    setUploaden(true);
    setUploadFout(null);
    try {
      const r = await api.projects.parse(file, ploegId);
      const w = weergaveRegels(r.matching.matchedLines, r.matching.meerwerkLines, r.gevel);
      setUpload({
        bedrag: Math.round((r.totals.subtotaal + r.totals.meerwerk) * 100) / 100,
        bronReferentie: `opstartfiche:${r.pdfFilename}`,
        bronLabel: `Opgeladen opstartfiche ${r.pdfFilename}`,
        omschrijving: `Werken project ${projectNummer} volgens opstartfiche`,
        prijslijst: r.gevel ? `gevel variant ${r.gevel.variant}` : '',
        regels: [...w.werkposten, ...w.meerwerk],
        nietGekoppeld: [...r.matching.reviewItems, ...r.matching.unmatchedItems].map((x) => x.parsedItem.werken),
      });
    } catch (e) {
      setUploadFout(e instanceof ApiError && e.payload && typeof e.payload === 'object' && 'error' in e.payload ? String((e.payload as { error: string }).error) : 'Opladen mislukt');
    } finally {
      setUploaden(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" role="dialog" aria-modal="true" aria-labelledby="voorstel-titel">
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto normal-case tracking-normal">
        <h2 id="voorstel-titel" className="text-xl font-bold mb-1">Voorstel extra werk — {projectNummer}</h2>
        <p className="text-sm text-secondary mb-4">Berekend met de prijslijst van deze onderaannemer, uit de opstartfiche van het gelinkte project. Nooit de verkoopprijs aan de klant.</p>

        {q.isLoading && <p className="text-sm">Voorstel berekenen…</p>}
        {!voorstel && (geenFiche || (q.isError && !geenFiche)) && (
          <div className="p-3 rounded-md bg-warning-100 border-l-4 border-warning text-sm mb-3">
            {foutTekst ?? 'Voorstel kon niet berekend worden.'}
            <div className="mt-2">
              <label className="btn-ghost btn-sm cursor-pointer inline-block">
                {uploaden ? 'Fiche lezen…' : 'Opstartfiche opladen (PDF)'}
                <input type="file" accept="application/pdf" className="hidden" disabled={uploaden} onChange={(e) => { const f = e.target.files?.[0]; if (f) void laadOp(f); }} />
              </label>
            </div>
            {uploadFout && <p className="text-danger mt-2">{uploadFout}</p>}
          </div>
        )}

        {voorstel && (
          <>
            <div className="text-xs text-secondary mb-2">Bron: {voorstel.bronLabel}{voorstel.prijslijst ? ` · ${voorstel.prijslijst}` : ''}</div>
            <table className="w-full text-sm mb-3">
              <thead>
                <tr className="text-xs uppercase tracking-allcaps border-b border-border">
                  <th className="text-left py-1">Post</th>
                  <th className="text-right py-1 w-28">Hoeveelheid</th>
                  <th className="text-right py-1 w-28">Prijs</th>
                  <th className="text-right py-1 w-28">Bedrag</th>
                </tr>
              </thead>
              <tbody>
                {voorstel.regels.map((r, i) => (
                  <tr key={i} className="border-b border-lightgrey">
                    <td className="py-1">{r.naam}{r.toelichting && <div className="text-xs text-secondary">{r.toelichting}</div>}</td>
                    <td className="py-1 text-right tabular-nums">{fmtNum(r.hoeveelheid)} {r.eenheid}</td>
                    <td className="py-1 text-right tabular-nums">{r.prijs == null ? 'varieert' : fmtEUR(r.prijs)}</td>
                    <td className="py-1 text-right tabular-nums font-bold">{fmtEUR(r.subtotaal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td colSpan={3} className="py-2 text-right font-bold">Voorstel</td><td className="py-2 text-right font-extrabold tabular-nums">{fmtEUR(voorstel.bedrag)}</td></tr>
              </tfoot>
            </table>
            {voorstel.nietGekoppeld.length > 0 && (
              <p className="text-xs text-warning mb-3">Niet automatisch gekoppeld en dus niet meegerekend: {voorstel.nietGekoppeld.join(', ')}.</p>
            )}
            {!magBevestigen && <p className="text-sm text-secondary mb-2">Enkel Eranda, Timen, Ervin of Jordy kunnen dit voorstel bevestigen.</p>}
          </>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" className="btn-ghost" onClick={onSluit}>Annuleren</button>
          <button
            type="button"
            className="btn-primary"
            disabled={!voorstel || !magBevestigen}
            onClick={() => voorstel && onBevestig({ bedrag: voorstel.bedrag, bronReferentie: voorstel.bronReferentie, omschrijving: voorstel.omschrijving })}
          >
            Bevestig {voorstel ? fmtEUR(voorstel.bedrag) : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
